import React, { useState, useEffect } from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Collapse,
    List,
    ListItem,
    ListItemText,
    Chip,
    IconButton,
    Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloseIcon from '@mui/icons-material/Close';
import UndoIcon from '@mui/icons-material/Undo';
import CircularProgress from '@mui/material/CircularProgress';

/**
 * A stable identity for a finding, so we can track which ones the user has
 * chosen to ignore even though findings are keyed by array index in the list.
 */
const findingKey = (finding, index) =>
    `${finding.sev}|${finding.rule}|${finding.unit}|${finding.msg}|${index}`;

/**
 * A signature for the whole set of findings. When this changes (e.g. after a
 * fix round re-lints the report and returns a different set), the component's
 * per-finding ignore state and dismissal are stale and must be reset — otherwise
 * the "Fix N warnings" label and counts desync from the current lint.
 */
const findingsSignature = (findings) =>
    (findings || []).map((f, i) => findingKey(f, i)).join('||');

/**
 * Surfaces the results of the TrustSpark report linter (see backend/report_linter.py).
 *
 * The linter checks every prose claim in the report against the numbers in the
 * report's own charts/tables. FAIL findings contradict the data; WARN findings
 * are likely issues that need a human look.
 *
 * When there are FAIL findings we render an error alert; when there are only
 * WARN findings we render a milder warning. If an `onRegenerate` handler is
 * provided (i.e. the user owns this report), we offer a re-generate button so
 * they can try to produce a clean report. If an `onFix` handler is provided we
 * also offer a "Fix warnings with AI" button and per-finding ignore controls.
 *
 * (The "auto-fix before showing the report" toggle lives in the Generate New
 * Report form, not here — see ReportGenerator.js.)
 *
 * Each finding has an "ignore" (✕) button. Ignored findings are struck through
 * and excluded from the fix. When the user clicks "Fix warnings with AI", only
 * the findings they kept are sent to `onFix(keptFindings)`. When every finding
 * is ignored (or the box is dismissed with the top-right ✕), the box disappears
 * entirely.
 */
const LintWarning = ({
    lint,
    onRegenerate,
    regenerating = false,
    onFix,
    fixing = false,
}) => {
    const [expanded, setExpanded] = useState(false);
    // Set of findingKey()s the user has chosen to ignore.
    const [ignored, setIgnored] = useState(() => new Set());
    // The user dismissed the whole box (top-right ✕).
    const [dismissed, setDismissed] = useState(false);

    const findings =
        lint && Array.isArray(lint.findings) ? lint.findings : [];
    const signature = findingsSignature(findings);

    // Whenever the findings change (a fix round re-lints and returns a new set),
    // reset the ignore/dismiss state so the box always reflects the current lint.
    useEffect(() => {
        setIgnored(new Set());
        setDismissed(false);
    }, [signature]);

    if (findings.length === 0 || dismissed) {
        return null;
    }

    const keptFindings = findings.filter(
        (finding, index) => !ignored.has(findingKey(finding, index))
    );

    // If the user has ignored every finding, the callout should go away entirely.
    if (keptFindings.length === 0) {
        return null;
    }

    const keptFail = keptFindings.filter((f) => f.sev === 'FAIL').length;

    const fail = lint.fail || 0;
    const warn = lint.warn || 0;
    const severity = fail > 0 ? 'error' : 'warning';

    let title;
    if (fail > 0) {
        title = `This report has ${fail} statement${fail === 1 ? '' : 's'} that contradict${fail === 1 ? 's' : ''} its own data`;
    } else {
        title = `This report has ${warn} statement${warn === 1 ? '' : 's'} that may need review`;
    }

    const toggleIgnore = (key) => {
        setIgnored((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const handleIgnoreAll = () => {
        // Ignore every currently-visible finding; keptFindings becomes empty and
        // the box hides itself on the next render.
        setIgnored(new Set(findings.map((f, i) => findingKey(f, i))));
    };

    const handleDismiss = () => setDismissed(true);

    const handleFix = () => {
        if (onFix) {
            onFix(keptFindings);
        }
    };

    // Label the fix button with how many warnings will actually be sent.
    let fixLabel;
    if (fixing) {
        fixLabel = 'Fixing…';
    } else if (ignored.size > 0) {
        fixLabel = `Fix ${keptFindings.length} warning${keptFindings.length === 1 ? '' : 's'} with AI`;
    } else {
        fixLabel = 'Fix warnings with AI';
    }

    return (
        <Alert severity={severity} sx={{ mb: 3 }} onClose={handleDismiss}>
            <AlertTitle>{title}</AlertTitle>
            An automated check compared the report's written claims against the numbers in
            its charts and tables and found the issues below. We recommend reviewing them
            {onFix ? ', ignoring any that are acceptable, fixing the rest automatically with AI,' : ''}
            {onRegenerate ? ' or re-generating the report.' : '.'}

            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                    size="small"
                    color="inherit"
                    onClick={() => setExpanded((prev) => !prev)}
                    endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                    {expanded ? 'Hide details' : `Show ${findings.length} finding${findings.length === 1 ? '' : 's'}`}
                </Button>
                {onFix && (
                    <Button
                        size="small"
                        variant="contained"
                        color={keptFail > 0 ? 'error' : 'warning'}
                        startIcon={fixing ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
                        onClick={handleFix}
                        disabled={fixing || regenerating || keptFindings.length === 0}
                    >
                        {fixLabel}
                    </Button>
                )}
                {onFix && (
                    <Button
                        size="small"
                        color="inherit"
                        onClick={handleIgnoreAll}
                        disabled={fixing || regenerating}
                    >
                        Ignore all
                    </Button>
                )}
                {onRegenerate && (
                    <Button
                        size="small"
                        variant={onFix ? 'outlined' : 'contained'}
                        color={fail > 0 ? 'error' : 'warning'}
                        startIcon={<AutorenewIcon />}
                        onClick={onRegenerate}
                        disabled={regenerating || fixing}
                    >
                        {regenerating ? 'Re-generating…' : 'Re-generate report'}
                    </Button>
                )}
            </Box>

            {onFix && ignored.size > 0 && (
                <Box sx={{ mt: 1, fontSize: '0.85rem', fontStyle: 'italic' }}>
                    {ignored.size} warning{ignored.size === 1 ? '' : 's'} ignored — only the
                    remaining {keptFindings.length} will be sent to the AI.
                </Box>
            )}

            <Collapse in={expanded}>
                <List dense sx={{ mt: 1 }}>
                    {findings.map((finding, index) => {
                        const key = findingKey(finding, index);
                        const isIgnored = ignored.has(key);
                        return (
                            <ListItem
                                key={key}
                                sx={{
                                    alignItems: 'flex-start',
                                    py: 0.5,
                                    opacity: isIgnored ? 0.5 : 1,
                                }}
                                secondaryAction={
                                    onFix ? (
                                        <Tooltip title={isIgnored ? 'Restore this warning' : 'Ignore this warning'}>
                                            <IconButton
                                                edge="end"
                                                size="small"
                                                aria-label={isIgnored ? 'restore warning' : 'ignore warning'}
                                                onClick={() => toggleIgnore(key)}
                                                disabled={fixing}
                                            >
                                                {isIgnored ? <UndoIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
                                            </IconButton>
                                        </Tooltip>
                                    ) : null
                                }
                            >
                                <Chip
                                    label={finding.sev}
                                    size="small"
                                    color={finding.sev === 'FAIL' ? 'error' : 'warning'}
                                    sx={{ mr: 1, mt: 0.25, fontWeight: 'bold' }}
                                />
                                <ListItemText
                                    primary={finding.msg}
                                    secondary={`${finding.rule} — ${finding.unit}`}
                                    sx={{
                                        textDecoration: isIgnored ? 'line-through' : 'none',
                                        pr: onFix ? 4 : 0,
                                    }}
                                />
                            </ListItem>
                        );
                    })}
                </List>
            </Collapse>
        </Alert>
    );
};

export default LintWarning;
